import * as React from 'react'
import { cn } from '@/lib/utils'
import { H3, Small } from '@/components/ui/typography'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'outlined' | 'flat' | 'hover'
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | 'none'
  shadow?: 'none' | 'sm' | 'md' | 'lg'
  padding?: 'none' | 'sm' | 'md' | 'lg'
  bordered?: boolean
}

const variantClasses = {
  default: 'bg-card text-card-foreground',
  outlined: 'bg-transparent border border-border',
  flat: 'bg-transparent',
  hover: 'bg-card text-card-foreground transition-all hover:shadow-md cursor-pointer',
}

const paddingClasses = {
  none: 'p-0',
  sm: 'p-3 sm:p-4',
  md: 'p-4 sm:p-5 md:p-6',
  lg: 'p-6 sm:p-7 md:p-8',
}

const roundedClasses = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-lg', // Softer than QUBINEX (rounded-md)
  lg: 'rounded-xl', // Softer, friendlier
  xl: 'rounded-2xl',
}

const shadowClasses = {
  none: 'shadow-none',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      className,
      variant = 'default',
      rounded = 'lg', // Default to softer rounded
      shadow = 'sm',
      padding,
      bordered,
      ...props
    },
    ref
  ) => {
    const shouldApplyPadding = padding !== undefined
    const paddingClass = shouldApplyPadding ? paddingClasses[padding] : ''
    const roundedClass = roundedClasses[rounded]
    const shadowClass = shadowClasses[shadow]

    return (
      <div
        ref={ref}
        className={cn(
          variantClasses[variant],
          roundedClass,
          shadowClass,
          paddingClass,
          bordered && 'border border-border',
          !bordered && variant === 'default' && 'border border-border',
          'transition-all w-full',
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)
Card.displayName = 'Card'

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-4 md:p-6', className)}
    {...props}
  />
))
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <H3
    ref={ref}
    className={cn('leading-none tracking-tight', className)}
    {...props}
  />
))
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <Small
    ref={ref}
    className={cn('text-muted-foreground', className)}
    {...props}
  />
))
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-4 md:p-6 pt-0', className)} {...props} />
))
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex flex-col sm:flex-row items-stretch sm:items-center justify-end p-4 md:p-6 pt-0 gap-2',
      className
    )}
    {...props}
  />
))
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }

